/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Story Entry Page — "Enter your code" landing page
 *
 * This page accepts a document code/ID from a printed report's QR code
 * and navigates to the animated story view for that document.
 */

import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';

function StoryEntryPage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [showDocIdInput, setShowDocIdInput] = useState(false);
  const [docId, setDocId] = useState('');
  const navigate = useNavigate();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedCode = code.trim().toUpperCase();

    if (!trimmedCode) {
      setError('Please enter a document code');
      return;
    }

    // Basic validation: document codes start with DOC_ or are at least 8 chars
    if (trimmedCode.length < 8 && !trimmedCode.startsWith('DOC_')) {
      setError('Code should be at least 8 characters or start with DOC_');
      return;
    }

    // Navigate to the story view for this code
    navigate(`/story/${trimmedCode.toLowerCase()}`);
  };

  const handleDocIdSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedDocId = docId.trim();

    if (!trimmedDocId) {
      setError('Please enter a document ID');
      return;
    }

    // Navigate to Site-Visit Mode with the docId
    navigate(`/hub/${encodeURIComponent(trimmedDocId)}`);
  };

  return (
    <div className="story-entry-page">
      <div className="story-entry-container">
        <div className="story-entry-header">
          <h1>Your Heating Recommendation</h1>
          <p className="story-entry-subtitle">
            A personalised explanation prepared for your home
          </p>
        </div>

        <div className="story-entry-body">
          <p>
            This interactive explanation accompanies a heating recommendation prepared specifically for your home. 
            Enter the reference code from your document to view how the advice fits your property, usage, and priorities.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="story-entry-form">
          <div className="story-entry-input-group">
            <label htmlFor="document-code" className="story-entry-label">
              Reference code
            </label>
            <input
              type="text"
              id="document-code"
              className="story-entry-input"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError('');
              }}
              placeholder="e.g., DOC_ABC123XYZ"
              autoComplete="off"
              autoCapitalize="characters"
            />
            {error && <p className="story-entry-error">{error}</p>}
          </div>

          <button type="submit" className="story-entry-button">
            View my recommendation
          </button>
        </form>

        <div className="story-entry-reassurance">
          <p>
            This link is private and tied to your recommendation.
          </p>
        </div>

        <div className="story-entry-reassurance" style={{ marginTop: '12px' }}>
          <p>
            <Link to="/hub">Open Site-Visit Mode →</Link>
          </p>
        </div>

        <div className="story-entry-alternate" style={{
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          {!showDocIdInput ? (
            <button
              type="button"
              onClick={() => setShowDocIdInput(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#2563eb',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: '8px 16px'
              }}
            >
              Already have a link? Load saved report →
            </button>
          ) : (
            <form onSubmit={handleDocIdSubmit} style={{ maxWidth: '400px', margin: '0 auto' }}>
              <div className="story-entry-input-group">
                <label htmlFor="doc-id-input" className="story-entry-label">
                  Document ID
                </label>
                <input
                  type="text"
                  id="doc-id-input"
                  className="story-entry-input"
                  value={docId}
                  onChange={(e) => {
                    setDocId(e.target.value);
                    setError('');
                  }}
                  placeholder="doc_abc123xyz"
                  autoComplete="off"
                  style={{ fontSize: '14px' }}
                />
                {error && <p className="story-entry-error">{error}</p>}
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button type="submit" className="story-entry-button" style={{ flex: 1 }}>
                  Load Report
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDocIdInput(false);
                    setDocId('');
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default StoryEntryPage;
